/**
 * Accepts 2 functions, a and b. Also accepts the return values of both functions for generic typing.
 * Returns function a or b based on the value of the use param.

 * Example: a and b are not async functions

  const returnNum = () => 1;
  const returnStr = () => "string";
  const functionToUse = ABFunction<number, string>(
    returnNum,
    returnStr,
    "a"
  );
  const response = functionToUse() as number;

 *************************************************

 * Example: either a or b (or both) are async functions

  const fetchNum = () => axios.get("some api value");
  const returnStr = () => "string";
  const functionToUse = ABFunction<Promise<AxiosResponse<number>>>, string>(
    fetchNum,
    returnStr,
    "a"
  );
  const response = (await functionToUse()) as number;
 */

export function noop(): void {
  // intentionally empty
}

// define the implementation
export function ABFunction<A, B>(
  a: (...args: unknown[]) => A,
  b: (...args: unknown[]) => B,
  use: "a" | "b"
) {
  return use === "a" ? a : b;
}

type ExcludeFunctions<T> = {
  [K in keyof T as T[K] extends (...args: never) => unknown ? never : K]: T[K];
};

/**
 * Removes all functions from an object.
 *
 * This utility function iterates over the key/value pairs of the given object and
 * removes the pair whose values are functions.
 * It returns a new object with only the non-function properties.
 *
 * @template T - The type of the input object.
 * @param {T} obj - The object from which to remove functions.
 * @returns {ExcludeFunctions<T>} A new object with all functions removed.
 */
export function removeFunctions<T extends Record<string, unknown>>(
  obj: T
): ExcludeFunctions<T> {
  const result: Partial<ExcludeFunctions<T>> = {};
  const keys = Object.keys(obj) as Array<keyof T>;

  for (let i = 0; i < keys.length; i += 1) {
    const key = keys[i];
    const value = obj[key];
    if (typeof value !== "function") {
      // Explicitly cast the key and value to the expected types
      (result as Record<keyof T, unknown>)[key] = value;
    }
  }

  return result as unknown as ExcludeFunctions<T>;
}
